import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2, RefreshCw, TrendingUp } from "lucide-react";
import { apiGet } from "@/lib/apiAdmin";
import { CoachAdvice } from "./CoachAdvice";

const fmt = (n) => `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
const shift = (m, d) => { const [y, mo] = m.split("-").map(Number); const t = new Date(y, mo - 1 + d, 1); return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}`; };
const label = (m) => new Date(`${m}-01T12:00:00`).toLocaleDateString("en-US", { month: "long", year: "numeric" });

const JobRow = ({ j }) => (
  <div className="py-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm" data-testid={`coach-job-${j.booking_id}`}>
    <span className="text-gray-500 text-xs w-14">{new Date(`${j.date}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
    <span className="text-white min-w-[110px]">{j.customer}{j.car ? <span className="text-gray-500"> · {j.car}</span> : null}</span>
    <span className="text-xs text-gray-400 flex-1 min-w-[140px]">{j.services.join(", ")}{j.customer_type === "contractor" ? " · contractor" : ""}</span>
    <span className="text-xs text-gray-400">charged <span className="text-white">{fmt(j.charged)}</span> → could be <span className="text-emerald-300">{fmt(j.could_have)}</span></span>
    <span className="text-xs px-2 py-0.5 rounded-full border border-amber-400/30 text-amber-300" data-testid="coach-job-missed">+{fmt(j.missed)}</span>
    <span className="w-full sm:w-auto text-[11px] text-gray-500">below {j.below.join(" & ")}</span>
  </div>
);

export const PriceCoach = () => {
  const [month, setMonth] = useState(() => new Date().toLocaleDateString("en-CA", { timeZone: "America/Phoenix" }).slice(0, 7));
  const [r, setR] = useState(null);
  useEffect(() => { setR(null); apiGet("/admin/coach/report", { month }).then(setR).catch(() => setR({ underpriced: [], missed_total: 0, jobs: 0, market_rates: {} })); }, [month]);
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5 space-y-4" data-testid="price-coach-report">
      <div className="flex flex-wrap items-center gap-3">
        <TrendingUp className="w-4 h-4 text-amber-300" />
        <p className="text-white font-['Outfit'] flex-1 whitespace-nowrap">Price Coach report</p>
        <div className="flex items-center gap-2 w-full sm:w-auto justify-between">
          <button onClick={() => setMonth(shift(month, -1))} className="p-1.5 rounded-lg border border-white/10 text-gray-400 hover:text-white transition-colors" data-testid="coach-prev-month"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-sm text-gray-300 w-32 text-center" data-testid="coach-month">{label(month)}</span>
          <button onClick={() => setMonth(shift(month, 1))} className="p-1.5 rounded-lg border border-white/10 text-gray-400 hover:text-white transition-colors" data-testid="coach-next-month"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>
      {!r ? <Loader2 className="w-5 h-5 animate-spin text-gray-500" /> : <>
        <div className="flex flex-wrap items-end gap-6">
          <div><p className="text-[11px] text-gray-500">Could have earned</p><p className="text-2xl font-['Outfit'] text-amber-300" data-testid="coach-missed-total">+{fmt(r.missed_total)}</p></div>
          <p className="text-xs text-gray-500 pb-1" data-testid="coach-summary">{r.underpriced.length} of {r.jobs} job{r.jobs === 1 ? "" : "s"} underpriced · compared with your list price, JARVIS's quote and the area market</p>
        </div>
        {r.underpriced.length === 0
          ? <p className="text-sm text-gray-500" data-testid="coach-empty">No underpriced jobs this month — nicely done.</p>
          : <div className="divide-y divide-white/5">{r.underpriced.map((j) => <JobRow key={j.booking_id} j={j} />)}</div>}
        <MarketRates rates={r.market_rates} />
      </>}
      <CoachAdvice month={month} />
    </div>
  );
};

const MarketRates = ({ rates }) => {
  const rows = Object.entries(rates || {}).filter(([, v]) => v.typical);
  if (!rows.length) return null;
  return (
    <details className="text-xs text-gray-400" data-testid="coach-market-rates">
      <summary className="cursor-pointer text-gray-400 hover:text-white transition-colors flex items-center gap-1"><RefreshCw className="w-3 h-3" />Area market rates (AI estimate, refreshed monthly)</summary>
      <div className="grid sm:grid-cols-2 gap-x-6 mt-2">
        {rows.map(([k, v]) => <div key={k} className="flex justify-between py-1 border-b border-white/5"><span>{k}</span><span className="text-gray-300">{fmt(v.low)}–{fmt(v.high)} · typ {fmt(v.typical)}</span></div>)}
      </div>
    </details>
  );
};
